import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    IconButton,
    Radio,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { AutorenewOutlined, CloseOutlined, DeleteOutline, EditOutlined, MailOutline } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { isWildcardRecipient, signerCandidates, wildcardDomain } from "@/deal-room/recipient-domains";
import { roomTypeLabel, type RoomType } from "@/deal-room/room-path";
import { ds } from "@/deal-room/theme/ds";

import { formatDay } from "./format";
import { bcRow, bcTableFrame, bcTd, bcTh, siteInput } from "./table-styles";

// The access panel: a room's recipients, invites and pending access requests,
// opened from the staff directory row (2026-09-24, Bill). The recipient list
// is the room's whole permission model (docs/adr/0001). Everything here goes
// through /api/rooms/{slug}/recipients, /invite and /access-requests/dismiss
// with the staff session; the app never reaches Pipedrive, so a deal room's
// contacts are pulled from the hub and the panel says so.

interface Recipient {
    email: string;
    name?: string;
    role?: string;
    lastInvited?: string;
    lastOpened?: string;
}

interface PendingRequest {
    email: string;
    at: string;
    count: number;
}

interface PanelData {
    slug: string;
    roomType: RoomType;
    supplierName?: string;
    owner?: { name?: string; email?: string };
    recipients: Recipient[];
    // The resolved signer: agreements sent for signature default to them.
    signerEmail?: string;
    pendingRequests: PendingRequest[];
}


const th = bcTh;
const td = bcTd;

async function api<T>(url: string, init?: RequestInit): Promise<T> {
    const res = await fetch(url, { credentials: "include", headers: { "Content-Type": "application/json" }, ...init });
    const body = (await res.json().catch(() => ({}))) as T & { error?: string; issues?: { message: string }[] };
    if (!res.ok) throw new Error(body.issues?.[0]?.message ?? body.error ?? `${res.status}`);
    return body;
}

export function AccessPanel({ slug, onClose, onChanged }: { slug: string | null; onClose: () => void; onChanged: () => void }) {
    return (
        <Drawer anchor="right" open={Boolean(slug)} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: 960 }, maxWidth: "100vw", p: 3 } }}>
            {slug && <PanelBody key={slug} slug={slug} onClose={onClose} onChanged={onChanged} />}
        </Drawer>
    );
}

// Keyed by slug from the Drawer, so switching rooms remounts it with fresh state.
function PanelBody({ slug, onClose, onChanged }: { slug: string; onClose: () => void; onChanged: () => void }) {
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [undo, setUndo] = useState<{ previous: Recipient[]; email: string } | null>(null);
    const [draft, setDraft] = useState({ email: "", name: "" });
    const [askInvite, setAskInvite] = useState<string | null>(null);
    const [askRemove, setAskRemove] = useState<string | null>(null);
    // Inline name edit (2026-10-29, Elijah): the name only, never the email.
    // A different address is a remove and an add, so access stays explicit.
    const [editing, setEditing] = useState<{ email: string; name: string } | null>(null);

    const query = useQuery({
        queryKey: ["room-access", slug],
        queryFn: () => api<PanelData>(`/api/rooms/${slug}/recipients`),
        retry: false,
    });
    const data = query.data ?? null;
    const loadError = query.error ? (query.error as Error).message : null;
    const load = async () => {
        await query.refetch();
    };

    const save = async (recipients: Recipient[], done?: string, signerEmail?: string) => {
        setBusy(true);
        try {
            await api(`/api/rooms/${slug}/recipients`, {
                method: "PUT",
                body: JSON.stringify({ recipients: recipients.map(({ email, name, role }) => ({ email, name, role })), signerEmail }),
            });
            await load();
            onChanged();
            if (done) setNotice(done);
            setError(null);
            return true;
        } catch (e) {
            setError((e as Error).message);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const invite = async (emails?: string[]) => {
        setBusy(true);
        try {
            const out = await api<{ sent: string[]; failed: { email: string }[] }>(`/api/rooms/${slug}/invite`, {
                method: "POST",
                body: JSON.stringify(emails ? { emails } : {}),
            });
            await load();
            setNotice(
                out.sent.length
                    ? `Invitation sent to ${out.sent.join(", ")}.`
                    : out.failed.length
                      ? `Could not send to ${out.failed.map((f) => f.email).join(", ")}.`
                      : "Nobody to invite: every recipient is staff or a company wildcard.",
            );
            setError(null);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    const add = async () => {
        if (!data) return;
        const email = draft.email.trim().toLowerCase();
        if (!email) return;
        const entry: Recipient = { email, name: draft.name.trim() || undefined };
        const ok = await save([...data.recipients, entry], `${email} can now sign in.`);
        setDraft({ email: "", name: "" });
        // A company wildcard has no inbox to invite.
        if (ok && !isWildcardRecipient(email)) setAskInvite(email);
    };

    // Only Save (or Enter) writes the name; X, Escape or leaving the field keeps it open or drops it.
    const saveName = async () => {
        const edit = editing;
        setEditing(null);
        if (!edit || !data) return;
        const name = edit.name.trim() || undefined;
        if (name === (data.recipients.find((r) => r.email === edit.email)?.name || undefined)) return;
        await save(
            data.recipients.map((r) => (r.email === edit.email ? { ...r, name } : r)),
            name ? `Name saved for ${edit.email}.` : `Name cleared for ${edit.email}.`,
        );
    };

    const remove = async (email: string) => {
        if (!data) return;
        const previous = data.recipients;
        await save(previous.filter((r) => r.email !== email));
        setUndo({ previous, email });
    };

    const admit = async (email: string) => {
        if (!data) return;
        await save([...data.recipients, { email }]);
        await invite([email]);
    };

    const dismiss = async (email: string) => {
        setBusy(true);
        try {
            await api(`/api/rooms/${slug}/access-requests/dismiss`, { method: "POST", body: JSON.stringify({ email }) });
            await load();
            onChanged();
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    const people = data?.recipients.filter((r) => !isWildcardRecipient(r.email)) ?? [];
    const canSign = new Set(signerCandidates(people).map((r) => r.email));
    const wildcards = data?.recipients.filter((r) => isWildcardRecipient(r.email)) ?? [];

    return (
        <>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="overline" color="text.secondary">
                        {data ? `${roomTypeLabel(data.roomType)} · ${data.slug}` : slug}
                    </Typography>
                    <Typography variant="h2" sx={{ fontSize: "1.5rem", mt: 0 }}>
                        {data?.supplierName ?? "Access"}
                    </Typography>
                    {data?.owner?.name && (
                        <Typography sx={{ fontSize: ds.text.sm, color: ds.gray500 }}>Room owner: {data.owner.name}</Typography>
                    )}
                </Box>
                <IconButton aria-label="Close" onClick={onClose} size="small">
                    <CloseOutlined />
                </IconButton>
            </Stack>

            <Typography sx={{ fontSize: ds.text.sm, color: ds.gray600, mb: 2 }}>
                Everyone listed here can sign in to this room. Bison staff always can. The signer is the person agreements are sent to for
                signature.
                {data?.roomType === "deal_room" && (
                    <>
                        {" "}
                        The contacts on the Pipedrive deal are pulled onto this list from the hub with{" "}
                        <Box component="code" sx={{ fontSize: 12 }}>
                            /deal-room {data.slug} invite
                        </Box>
                        .
                    </>
                )}
            </Typography>

            {(error ?? loadError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error ?? loadError}
                </Alert>
            )}

            {data && data.pendingRequests.length > 0 && (
                <Box sx={{ mb: 3, p: 2, border: `1px solid ${ds.warning.border}`, bgcolor: ds.warning.bg, borderRadius: `${ds.radius}px` }}>
                    <Typography sx={{ fontWeight: 600, fontSize: ds.text.sm, color: ds.warning.text, mb: 1 }}>
                        Asked for access and not on the list
                    </Typography>
                    <Stack spacing={1}>
                        {data.pendingRequests.map((p) => (
                            <Stack key={p.email} direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                                <Typography sx={{ fontSize: ds.text.sm, flex: 1, minWidth: 200 }}>
                                    {p.email}
                                    <Box component="span" sx={{ color: ds.gray500 }}>
                                        {" "}
                                        · asked {formatDay(p.at)}
                                        {p.count > 1 ? `, ${p.count} times` : ""}
                                    </Box>
                                </Typography>
                                <Button size="small" variant="contained" disabled={busy} onClick={() => admit(p.email)}>
                                    Add and invite
                                </Button>
                                <Button size="small" variant="outlined" disabled={busy} onClick={() => dismiss(p.email)}>
                                    Dismiss
                                </Button>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            )}

            <Box sx={{ ...bcTableFrame, mb: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={th}>Email</TableCell>
                        <TableCell sx={th}>Name</TableCell>
                        <TableCell sx={th}>Signer</TableCell>
                        <TableCell sx={th}>Last invited</TableCell>
                        <TableCell sx={th}>Last opened</TableCell>
                        <TableCell sx={th} align="right" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!data && !loadError && (
                        <TableRow>
                            <TableCell sx={td} colSpan={6}>
                                Loading…
                            </TableCell>
                        </TableRow>
                    )}
                    {data && data.recipients.length === 0 && (
                        <TableRow>
                            <TableCell sx={td} colSpan={6}>
                                Nobody outside Bison can sign in yet.
                            </TableCell>
                        </TableRow>
                    )}
                    {people.map((r) => (
                        <TableRow key={r.email} sx={bcRow}>
                            <TableCell sx={td}>{r.email}</TableCell>
                            <TableCell sx={{ ...td, py: 0.5, "&:hover .name-edit, &:focus-within .name-edit": { opacity: 1 } }}>
                                {editing?.email === r.email ? (
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <TextField
                                            size="small"
                                            autoFocus
                                            value={editing.name}
                                            onChange={(e) => setEditing({ email: r.email, name: e.target.value })}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") void saveName();
                                                if (e.key === "Escape") setEditing(null);
                                            }}
                                            sx={{ ...siteInput, "& .MuiOutlinedInput-input": { px: 1, py: 0.5 } }}
                                            slotProps={{ htmlInput: { "aria-label": `Name for ${r.email}`, "data-1p-ignore": true, "data-lpignore": "true" } }}
                                        />
                                        <Button size="small" variant="contained" disabled={busy} onClick={() => void saveName()}>
                                            Save
                                        </Button>
                                        <Tooltip title="Cancel">
                                            <IconButton size="small" aria-label="Cancel name edit" onClick={() => setEditing(null)} sx={{ color: ds.gray600 }}>
                                                <CloseOutlined sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <span>{r.name ?? ""}</span>
                                        <Tooltip title="Edit name">
                                            <span>
                                                <IconButton
                                                    className="name-edit"
                                                    size="small"
                                                    aria-label={`Edit name for ${r.email}`}
                                                    disabled={busy}
                                                    onClick={() => setEditing({ email: r.email, name: r.name ?? "" })}
                                                    sx={{ color: ds.gray600, opacity: 0, "&:focus-visible": { opacity: 1 } }}
                                                >
                                                    <EditOutlined sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                )}
                            </TableCell>
                            <TableCell sx={{ ...td, py: 0 }}>
                                {canSign.has(r.email) && (
                                    <Radio
                                        size="small"
                                        checked={data?.signerEmail === r.email}
                                        disabled={busy}
                                        onChange={() => data && save(data.recipients, `${r.name ?? r.email} is the signer.`, r.email)}
                                        slotProps={{ input: { "aria-label": `Make ${r.email} the signer` } }}
                                    />
                                )}
                            </TableCell>
                            <TableCell sx={td}>{formatDay(r.lastInvited)}</TableCell>
                            <TableCell sx={td}>{formatDay(r.lastOpened)}</TableCell>
                            <TableCell sx={{ ...td, whiteSpace: "nowrap", py: 0.5 }} align="right">
                                <Tooltip title={r.lastInvited ? "Invite again" : "Send the invitation"}>
                                    <span>
                                        <IconButton size="small" aria-label={r.lastInvited ? "Invite again" : "Invite"} disabled={busy} onClick={() => invite([r.email])} sx={{ color: ds.gray600 }}>
                                            {r.lastInvited ? <AutorenewOutlined fontSize="small" /> : <MailOutline fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Remove">
                                    <span>
                                        <IconButton size="small" aria-label="Remove" disabled={busy} onClick={() => setAskRemove(r.email)} sx={{ color: ds.gray600, "&:hover": { color: ds.danger.text } }}>
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                    {wildcards.map((r) => (
                        <TableRow key={r.email} sx={bcRow}>
                            <TableCell sx={td}>{r.email}</TableCell>
                            <TableCell sx={td} colSpan={4}>
                                Everyone at {wildcardDomain(r.email)}
                            </TableCell>
                            <TableCell sx={{ ...td, py: 0.5 }} align="right">
                                <Tooltip title="Remove">
                                    <span>
                                        <IconButton size="small" aria-label="Remove" disabled={busy} onClick={() => setAskRemove(r.email)} sx={{ color: ds.gray600, "&:hover": { color: ds.danger.text } }}>
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }} sx={{ mb: 2 }}>
                <TextField
                    size="small"
                    label="Email or *@company.com"
                    value={draft.email}
                    onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                    sx={{ ...siteInput, flex: 2 }}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { "data-1p-ignore": true, "data-lpignore": "true" } }}
                />
                <TextField
                    size="small"
                    label="Name"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    sx={{ ...siteInput, flex: 1.5 }}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <Button variant="contained" disabled={busy || !draft.email.trim()} onClick={add} sx={{ whiteSpace: "nowrap" }}>
                    Add
                </Button>
            </Stack>

            <Snackbar
                open={Boolean(undo)}
                autoHideDuration={6000}
                onClose={() => setUndo(null)}
                message={undo ? `${undo.email} removed.` : ""}
                action={
                    <Button
                        color="inherit"
                        size="small"
                        onClick={async () => {
                            const u = undo;
                            setUndo(null);
                            if (u) await save(u.previous, `${u.email} is back on the list.`);
                        }}
                    >
                        Undo
                    </Button>
                }
            />
            <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)} message={notice ?? ""} />
            <Dialog open={Boolean(askInvite)} onClose={() => setAskInvite(null)}>
                <DialogTitle>Send an email invitation?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: ds.text.sm }}>
                        {askInvite} can now sign in. Do you want to email them the invitation with a sign-in link now?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAskInvite(null)}>No</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            const email = askInvite;
                            setAskInvite(null);
                            if (email) await invite([email]);
                        }}
                    >
                        Yes, send it
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(askRemove)} onClose={() => setAskRemove(null)}>
                <DialogTitle>Remove access?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: ds.text.sm }}>
                        {askRemove && isWildcardRecipient(askRemove)
                            ? `Everyone at ${wildcardDomain(askRemove)} will no longer be able to sign in to this room.`
                            : `${askRemove} will no longer be able to sign in to this room.`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAskRemove(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={async () => {
                            const email = askRemove;
                            setAskRemove(null);
                            if (email) await remove(email);
                        }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
